import { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Upload,
  AlertCircle,
  Eye,
  ExternalLink,
  Clock,
  FileText,
  BarChart4,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  UserPlus,
  Users,
  Calendar,
  CalendarDays,
  Loader2,
  BookOpen,
  CheckCircle,
  Info,
} from "lucide-react";
import {
  useCreateEssayGrader,
  useGetEssayGrader,
  useGetEssayGraders,
  useCreateEssayAssignment,
  useGetEssayAssignments,
  useAssignStudents,
} from "@packages/api/madlenAPI";
import {
  TeacherDocumentResponseDto,
  EssayGradingResultDto,
  EssayGradingSubSingleResultDto,
  CreateEssayAssignmentRequestDto,
  NewEssayEvaluationResult,
} from "@packages/api/madlenAPI.schemas";
import { getEnvironment } from "@packages/utils";
import { useToast } from "@packages/contexts/ToastContext";
import { motion, AnimatePresence } from "framer-motion";
import { trackEvent } from "@packages/hooks/useAnalytics";
import { Button, Text } from "@core/index";
import { FileUploader } from "@core/file-uploader";
import { DateTimePicker } from "@core/datepicker";
import LoadingContainer from "@packages/components/Loading";
import { useAppSelector } from "@packages/store/hooks";
import { ChatMessageBox } from "@features/assistants/components/essay-grader/ChatMessageBox";
import { HistoryModal } from "@features/assistants/components/essay-grader/HistoryModal";
import ToleranceLevelSelector from "../../components/ToleranceLevelSelector";
import { ClassroomAndStudentSelector } from "@features/assignments/SingleAssignment/componnets/ClassroomAndStudentSelector";

// Interface for uploaded files
interface UploadedFile {
  type: "rubric" | "essay" | "assignment-instruction" | "instructions";
  url: string;
  name: string;
  fileId: number;
  response?: TeacherDocumentResponseDto;
}

// Analysis steps for the loading screen
const ANALYSIS_STEPS = [
  "assistant.essayGrader.steps.analyzingRubric",
  "assistant.essayGrader.steps.recognizingText",
  "assistant.essayGrader.steps.extractingContent",
  "assistant.essayGrader.steps.analyzingEssay",
  "assistant.essayGrader.steps.identifyingPatterns",
  "assistant.essayGrader.steps.evaluatingStrengths",
  "assistant.essayGrader.steps.matchingCriteria",
];

const EssayGradingView = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [searchParams, setSearchParams] = useSearchParams();
  const returnChatId = searchParams.get("chatId");
  const returnSubId = searchParams.get("subId");
  const returnView = searchParams.get("view");

  const [showHistory, setShowHistory] = useState(false);
  const [view, setView] = useState("upload");
  const [activeSection, setActiveSection] = useState("upload");
  const [uploadedEssayCount, setUploadedEssayCount] = useState(0);
  const [activeUploads, setActiveUploads] = useState(0);
  const [newChatId, setNewChatId] = useState<string>("");
  const [selectedEssayIndex, setSelectedEssayIndex] = useState<number | null>(
    null,
  );
  const [toleranceLevel, setToleranceLevel] = useState<number>(4);
  const [instructionsIncludedInRubric, setInstructionsIncludedInRubric] =
    useState<boolean>(false);
  const [showInstructionsInfo, setShowInstructionsInfo] =
    useState<boolean>(false);
  const resultsRef = useRef<HTMLDivElement>(null);

  const [gradingMode, setGradingMode] = useState<"self" | "assignment" | null>(
    null,
  );
  const [assignmentMode, setAssignmentMode] = useState<"TEXT" | "PDF">("TEXT");
  const [assignmentTopic, setAssignmentTopic] = useState("");
  const [assignmentTitle, setAssignmentTitle] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [isCreatingAssignment, setIsCreatingAssignment] = useState(false);

  const [assignmentInstructionFiles, setAssignmentInstructionFiles] = useState<
    UploadedFile[]
  >([]);

  const [selectedStudents, setSelectedStudents] = useState<
    {
      classroomId: number;
      studentIds: number[];
    }[]
  >([]);
  const [assignedStudentIds, setAssignedStudentIds] = useState<number[]>([]);

  // User preferences
  const teacherPreferencedLanguage = useAppSelector(
    (state) => state.user.teacher?.preferences?.language,
  );

  // State for uploaded files - FIXED: Separate rubric files for different modes
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [assignmentRubricFiles, setAssignmentRubricFiles] = useState<
    UploadedFile[]
  >([]);

  // API hooks
  const { mutateAsync: createEssayGrader } = useCreateEssayGrader();
  const { data: essayGraderData } = useGetEssayGrader(Number(newChatId), {
    query: {
      enabled: !!newChatId,
      refetchInterval: (data) => {
        const results = data.state.data?.results;
        if (
          results &&
          results.length > 0 &&
          results.filter(
            (result: EssayGradingSubSingleResultDto) =>
              !result.result?.overall_summary,
          ).length === 0
        ) {
          return false;
        }
        return 3000;
      },
    },
  });

  // Assignment API hooks
  const { mutateAsync: createEssayAssignment } = useCreateEssayAssignment();
  const { mutateAsync: assignStudentsToEssayAssignment } = useAssignStudents();
  const { data: essayAssignments, refetch: refetchAssignments } =
    useGetEssayAssignments({
      page: 1,
      size: 10,
    });

  // History data
  const { data: oldChatsRaw } = useGetEssayGraders(
    {
      size: 100,
    },
    {
      query: {
        enabled: true,
      },
    },
  );

  const oldChats = useMemo(() => {
    return oldChatsRaw?.data?.sort((a, b) => {
      return (
        new Date(b.createdAt || "").getTime() -
        new Date(a.createdAt || "").getTime()
      );
    });
  }, [oldChatsRaw]);

  // Analytics tracking
  const { trackEvent: track } = trackEvent();

  // Handle returning from chat with URL parameters
  useEffect(() => {
    if (returnChatId && returnView === "details") {
      setNewChatId(returnChatId);
      setView("results");
      setActiveSection("results");

      if (returnSubId) {
        // This will be handled in another useEffect when essayGraderData loads
      }

      // Clear the URL parameters
      setSearchParams({});
    }
  }, [returnChatId, returnView, returnSubId, setSearchParams]);

  // Handle setting selectedEssayIndex when returning from chat
  useEffect(() => {
    if (returnSubId && essayGraderData?.results) {
      const essayIndex = essayGraderData.results.findIndex(
        (result) => result.id === Number(returnSubId),
      );

      if (essayIndex !== -1) {
        setSelectedEssayIndex(essayIndex);
        setView("details");
        setActiveSection("details");
      }
    }
  }, [returnSubId, essayGraderData]);

  // Check if results are ready and update UI
  useEffect(() => {
    if (
      essayGraderData &&
      essayGraderData.results &&
      essayGraderData.results.length > 0 &&
      essayGraderData.results.filter((result) => result.result?.overall_summary)
        .length !== 0
    ) {
      if (essayGraderData.results[0].result?.lang_code) {
        const language = essayGraderData?.results[0].result?.lang_code;

        if (language === "en" || language === "en-US") {
          i18n.changeLanguage("en");
        } else if (language === "tr" || language === "tr-TR") {
          i18n.changeLanguage("tr");
        }
      }

      // Only set to results view if we're not returning from chat with a specific view
      if (!returnView) {
        setView("results");

        // Scroll to results section after a short delay
        setTimeout(() => {
          if (resultsRef.current) {
            resultsRef.current.scrollIntoView({ behavior: "smooth" });
          }
        }, 300);
      }
    }
  }, [essayGraderData, i18n, returnView]);

  // Handle clicking outside the instructions info tooltip
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showInstructionsInfo) {
        const target = event.target as Element;
        if (!target.closest(".instructions-info-tooltip")) {
          setShowInstructionsInfo(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showInstructionsInfo]);

  // Handle file upload success with proper state management
  const handleFileUploadSuccess =
    (type: "rubric" | "essay" | "assignment-instruction" | "instructions") =>
    async (url: string, response?: TeacherDocumentResponseDto) => {
      track(
        {
          category: "ContentFileUpload",
          action: `essay.grader.file.upload.success`,
          label: `EssayGrader file upload success`,
          nonInteraction: false,
        },
        {
          location: "/essay-grader/file-upload",
          element: "file-upload",
          essayGraderFileType: type,
        },
      );

      if (!response?.id) {
        addToast({
          title: t("assistant.uploadError"),
          description: t("assistant.uploadErrorDescription"),
          variant: "error",
          duration: 5000,
        });
        return;
      }

      const newFile: UploadedFile = {
        type,
        url,
        name: response.fileName || "",
        fileId: response.id!,
        response,
      };

      if (view === "assignment-setup") {
        if (type === "assignment-instruction") {
          setAssignmentInstructionFiles((prev) => [
            ...prev.filter((f) => f.type !== "assignment-instruction"),
            newFile,
          ]);
        } else if (type === "rubric") {
          setAssignmentRubricFiles((prev) => [
            ...prev.filter((f) => f.type !== "rubric"),
            newFile,
          ]);
        }
      } else {
        setUploadedFiles((prev) => [...prev, newFile]);
      }

      setActiveUploads((prev) => Math.max(0, prev - 1));
    };

  // Handle file upload error
  const handleFileUploadError = (error: string) => {
    addToast({
      title: t("assistant.uploadError"),
      description: error,
      variant: "error",
      duration: 5000,
    });

    setActiveUploads((prev) => Math.max(0, prev - 1));
  };

  // Handle file deletion with proper state management
  const handleFileDelete = (
    fileType: "rubric" | "essay" | "assignment-instruction" | "instructions",
    fileId?: number,
  ) => {
    if (view === "assignment-setup") {
      if (fileType === "assignment-instruction") {
        setAssignmentInstructionFiles((prev) =>
          fileId
            ? prev.filter((f) => f.fileId !== fileId)
            : prev.filter((f) => f.type !== fileType),
        );
        if (assignmentMode === "PDF") {
          setAssignmentTopic("");
        }
      } else if (fileType === "rubric") {
        setAssignmentRubricFiles((prev) =>
          fileId
            ? prev.filter((f) => f.fileId !== fileId)
            : prev.filter((f) => f.type !== fileType),
        );
      }
    } else {
      setUploadedFiles((prev) =>
        fileId
          ? prev.filter((f) => f.fileId !== fileId)
          : prev.filter((f) => f.type !== fileType),
      );
    }
  };

  // Can we start analysis?
  const canStartAnalysis = useMemo(() => {
    if (view === "assignment-setup") {
      const hasRubric = assignmentRubricFiles.some(
        (file) => file.type === "rubric",
      );

      let hasContentBasedOnMode = false;

      if (assignmentMode === "TEXT") {
        hasContentBasedOnMode = assignmentTopic.trim().length > 0;
      } else if (assignmentMode === "PDF") {
        hasContentBasedOnMode = assignmentInstructionFiles.length > 0;
      }

      const hasTitle = assignmentTitle.trim().length > 0;

      return hasRubric && hasContentBasedOnMode && hasTitle;
    } else {
      const hasRubric = uploadedFiles.some((file) => file.type === "rubric");
      const hasEssay = uploadedFiles.some((file) => file.type === "essay");
      const hasInstructions = uploadedFiles.some(
        (file) => file.type === "instructions",
      );

      const instructionsRequirementMet =
        instructionsIncludedInRubric || hasInstructions;

      return (
        hasRubric &&
        hasEssay &&
        instructionsRequirementMet &&
        activeUploads === 0 &&
        uploadedEssayCount > 0
      );
    }
  }, [
    uploadedFiles,
    assignmentRubricFiles,
    assignmentInstructionFiles,
    activeUploads,
    uploadedEssayCount,
    view,
    assignmentTopic,
    assignmentMode,
    assignmentTitle,
    instructionsIncludedInRubric,
  ]);

  // Start grading analysis
  const handleStartAnalysis = async () => {
    setView("loading");
    setActiveSection("loading");

    track(
      {
        category: "ContentGeneration",
        action: "essay.grader.generation",
        label: "EssayGrader generate click",
        nonInteraction: false,
      },
      {
        location: "/essay-grader/generate",
        element: "generate-button",
      },
    );

    setNewChatId("");

    try {
      const rubricFile = uploadedFiles.find((f) => f.type === "rubric");
      const essayFiles = uploadedFiles.filter((f) => f.type === "essay");
      const instructionsFile = uploadedFiles.find(
        (f) => f.type === "instructions",
      );

      if (!rubricFile?.fileId || essayFiles.length === 0) {
        console.log(rubricFile, essayFiles);
        throw new Error("Missing file IDs");
      }

      const requestData: any = {
        name: "Essay Grader",
        environment: getEnvironment(),
        rubricFileId: rubricFile.fileId,
        essayFileIds: essayFiles.map((f) => f.fileId),
        toleranceLevel: toleranceLevel,
        instructionsIncludedInRubric: instructionsIncludedInRubric,
      };

      if (!instructionsIncludedInRubric && instructionsFile?.fileId) {
        requestData.instructionsFileId = instructionsFile.fileId;
      }

      const newChat = await createEssayGrader({
        data: requestData,
      });

      const chatId = newChat.id;
      if (!chatId) {
        throw new Error("Failed to create new chat");
      }

      setNewChatId(String(chatId));
    } catch (error) {
      console.error("Error starting analysis:", error);
      addToast({
        title: t("assistant.analysisError"),
        description: t("assistant.analysisErrorDescription"),
        variant: "error",
        duration: 5000,
      });
      setUploadedFiles([]);
      setView("upload");
      setActiveSection("upload");
    }
  };

  // Handle assignment creation
  const handleCreateAssignment = async () => {
    if (!canStartAnalysis) {
      return;
    }

    setView("assignment-students");
    setActiveSection("assignment-students");

    track(
      {
        category: "ContentGeneration",
        action: "essay.assignment.create",
        label: "Essay Assignment create click",
        nonInteraction: false,
      },
      {
        location: "/essay-grader/assignment",
        element: "create-assignment-button",
      },
    );
  };

  // Handle student assignment with loading state
  const handleAssignToStudents = async () => {
    setIsCreatingAssignment(true);

    try {
      const rubricFile = assignmentRubricFiles.find((f) => f.type === "rubric");

      if (!rubricFile?.fileId) {
        addToast({
          title: t("assignment.assignmentError"),
          description:
            t("assignment.assignmentErrorDescription") ||
            "Rubric file is required",
          variant: "error",
          duration: 5000,
        });
        return;
      }

      const requestData: CreateEssayAssignmentRequestDto = {
        title: assignmentTitle.trim() || "Essay Assignment",
        description:
          assignmentMode === "TEXT"
            ? assignmentTopic.trim()
            : assignmentInstructionFiles[0]?.name || "PDF-based assignment",
        rubricFileId: rubricFile.fileId,
        toleranceLevel: toleranceLevel,
        ...(assignmentMode === "TEXT" && assignmentTopic.trim()
          ? { studentInstructionText: assignmentTopic.trim() }
          : {}),
        ...(assignmentMode === "PDF" && assignmentInstructionFiles.length > 0
          ? { studentInstructionFileId: assignmentInstructionFiles[0].fileId }
          : {}),
        ...(dueDate ? { dueDate: dueDate } : {}),
      };

      const assignment = await createEssayAssignment({
        data: requestData,
      });

      const studentAssignments = selectedStudents.map((classroom) => ({
        classroomId: classroom.classroomId,
        studentIds: classroom.studentIds,
      }));

      if (studentAssignments.length > 0) {
        await assignStudentsToEssayAssignment({
          id: assignment.id!,
          data: {
            items: studentAssignments,
          },
        });
      }

      addToast({
        title: t("assignment.assignmentCreated"),
        description: t("assignment.assignmentCreatedDescription"),
        variant: "success",
        duration: 5000,
      });

      await refetchAssignments();
      navigate(`/essay-assignments/${assignment.id}`);
    } catch (error) {
      console.error("Error creating assignment:", error);

      if (error && typeof error === "object" && "response" in error) {
        const errorWithResponse = error as { response?: { data?: unknown } };
        if (errorWithResponse.response?.data) {
          console.error("API Error Response:", errorWithResponse.response.data);
        }
      }

      addToast({
        title: t("assignment.assignmentError"),
        description: t("assignment.assignmentErrorDescription"),
        variant: "error",
        duration: 5000,
      });
    } finally {
      setIsCreatingAssignment(false);
    }
  };

  // Open history modal
  const handleClickHistory = () => {
    track(
      {
        category: "ContentHistory",
        action: "essay.grader.history",
        label: "EssayGrader history click",
        nonInteraction: false,
      },
      {
        location: "/essay-grader/history",
        element: "history-button",
      },
    );
    setShowHistory(true);
  };

  const handleSelectChat = (chatId: number, subId?: number) => {
    setShowHistory(false);
    i18n.changeLanguage(teacherPreferencedLanguage);
    if (subId) {
      navigate(
        `/assistants/new/ESSAY_GRADER/${chatId}?isEssayGrader=true&subId=${subId}`,
      );
    } else {
      navigate(`/assistants/new/ESSAY_GRADER/${chatId}?isEssayGrader=true`);
    }
  };

  // Reset app state
  const handleReset = () => {
    setUploadedFiles([]);
    setAssignmentRubricFiles([]);
    setAssignmentInstructionFiles([]);
    setUploadedEssayCount(0);
    setNewChatId("");
    setSelectedEssayIndex(null);
    setInstructionsIncludedInRubric(false);
    setView("upload");
    setActiveSection("upload");
    setGradingMode(null);
    setAssignmentMode("TEXT");
    setAssignmentTopic("");
    setAssignmentTitle("");
    setDueDate("");
    setSelectedStudents([]);
    setAssignedStudentIds([]);
    setIsCreatingAssignment(false);
  };

  const acceptedFileTypes = {
    "application/pdf": [".pdf"],
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
      ".docx",
    ],
    "text/plain": [".txt"],
    "image/png": [".png"],
    "image/jpeg": [".jpg", ".jpeg"],
    "image/jpg": [".jpg", ".jpeg"],
  };

  // Helper function to get total score from result
  const getTotalScore = (
    result: NewEssayEvaluationResult | null | undefined,
  ) => {
    if (!result) return null;

    if ("total_score" in result && result.total_score !== undefined) {
      return result.total_score;
    }

    if (result.criteria_evaluations && result.criteria_evaluations.length > 0) {
      const totalScore = result.criteria_evaluations.reduce((sum, criteria) => {
        return sum + (criteria.score || 0);
      }, 0);
      return totalScore;
    }

    return null;
  };

  // Format date to show only day and month
  const formatShortDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language, {
      day: "numeric",
      month: "long",
    });
  };

  const renderEssayTable = () => {
    const results = (essayGraderData as EssayGradingResultDto | undefined)
      ?.results;
    if (!results || results.length === 0) {
      return null;
    }

    const sampleResult = results.find(
      (result) =>
        result.result?.criteria_evaluations &&
        result.result.criteria_evaluations.length > 0,
    );
    const criteriaEvaluations =
      sampleResult?.result?.criteria_evaluations || [];

    return (
      <div className="w-full">
        {/* Desktop View - Hidden on mobile and tablet */}
        <div className="hidden xl:block overflow-x-auto">
          <table className="w-full min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th
                  scope="col"
                  className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                >
                  {t("assistant.essayGrader.essayName") || "Kompozisyon Adı"}
                </th>
                <th
                  scope="col"
                  className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                >
                  {t("assistant.essayGrader.totalScore") || "Toplam Puan"}
                </th>
                {criteriaEvaluations.map((criterion) => (
                  <th
                    key={criterion.criterion_name}
                    scope="col"
                    className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider max-w-[120px] truncate"
                  >
                    {criterion.criterion_name}
                  </th>
                ))}
                <th
                  scope="col"
                  className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                >
                  {t("essayAssignments.actions.title") || "İşlemler"}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {results.map((essay, index) => {
                const totalScore = getTotalScore(essay.result);

                return (
                  <tr
                    key={essay.id}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-800 ${
                      selectedEssayIndex === index
                        ? "bg-blue-50 dark:bg-blue-900/20"
                        : ""
                    }`}
                  >
                    <td className="px-3 py-4 text-sm font-medium text-gray-900 dark:text-white max-w-[150px] truncate">
                      {essay.essayFiles?.[0]?.fileName ||
                        `Kompozisyon ${index + 1}`}
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-500 dark:text-gray-300 font-bold">
                      {totalScore !== null && typeof totalScore === "number" ? (
                        <span>{totalScore}</span>
                      ) : (
                        <div className="flex items-center justify-start">
                          <div className="h-4 w-4 bg-blue-500 rounded-full animate-pulse"></div>
                        </div>
                      )}
                    </td>
                    {criteriaEvaluations.map((criterion) => {
                      const essayCriterion =
                        essay.result?.criteria_evaluations?.find(
                          (c) => c.criterion_name === criterion.criterion_name,
                        );
                      return (
                        <td
                          key={criterion.criterion_name}
                          className="px-3 py-4 text-sm text-gray-500 dark:text-gray-300"
                        >
                          {essayCriterion?.score !== undefined ? (
                            essayCriterion.score
                          ) : (
                            <div className="flex items-center justify-start">
                              <div className="h-4 w-4 bg-blue-500 rounded-full animate-pulse"></div>
                            </div>
                          )}
                        </td>
                      );
                    })}
                    <td className="px-3 py-4 text-sm text-right">
                      <div className="flex flex-col sm:flex-row gap-1 justify-end">
                        <Button
                          onClick={() => {
                            if (!essay.result) {
                              addToast({
                                title:
                                  t("assistant.essayGrader.noResult") ||
                                  "Sonuç Yok",
                                description:
                                  t(
                                    "assistant.essayGrader.noResultDescription",
                                  ) || "Henüz analiz sonucu bulunmuyor",
                                variant: "info",
                                duration: 5000,
                              });
                              return;
                            }
                            setSelectedEssayIndex(index);
                            setView("details");
                            setActiveSection("details");
                          }}
                          size="sm"
                          rounded="lg"
                          bgColor="bg-blue-600 hover:bg-blue-700"
                          textColor="text-white"
                          className="inline-flex items-center justify-center"
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          <span className="hidden sm:inline">
                            {t("assistant.essayGrader.overview") ||
                              "Genel Bakış"}
                          </span>
                          <span className="sm:hidden">Görüntüle</span>
                        </Button>
                        <Button
                          onClick={() => {
                            if (
                              !essay.result ||
                              !essay.messages ||
                              essay.messages.length === 0
                            ) {
                              addToast({
                                title:
                                  t("assistant.essayGrader.noResult") ||
                                  "Sonuç Yok",
                                description:
                                  t(
                                    "assistant.essayGrader.noResultDescription",
                                  ) || "Henüz analiz sonucu bulunmuyor",
                                variant: "info",
                                duration: 5000,
                              });
                              return;
                            }

                            setUploadedFiles([]);
                            handleSelectChat(Number(newChatId), essay.id);
                          }}
                          size="sm"
                          rounded="lg"
                          bgColor="bg-gray-600 hover:bg-gray-700"
                          textColor="text-white"
                          className="inline-flex items-center justify-center"
                        >
                          <ExternalLink className="h-3 w-3 mr-1" />
                          <span className="hidden sm:inline">
                            {t("assistant.essayGrader.chatPage") ||
                              "Sohbet Sayfası"}
                          </span>
                          <span className="sm:hidden">Sohbet</span>
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Tablet View - Grid layout for tablets */}
        <div className="hidden md:grid xl:hidden grid-cols-1 gap-4">
          {results.map((essay, index) => {
            const totalScore = getTotalScore(essay.result);

            return (
              <div
                key={essay.id}
                className={`bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden ${
                  selectedEssayIndex === index
                    ? "border-blue-300 dark:border-blue-600"
                    : ""
                }`}
              >
                <div className="p-4">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white truncate">
                        {essay.essayFiles?.[0]?.fileName ||
                          `Kompozisyon ${index + 1}`}
                      </h3>
                    </div>
                    <div className="ml-4 flex items-center">
                      <div className="bg-blue-50 dark:bg-blue-900/20 px-3 py-2 rounded-lg">
                        <Text
                          variant="span"
                          className="text-sm font-bold text-blue-700 dark:text-blue-300"
                        >
                          {totalScore !== null &&
                          typeof totalScore === "number" ? (
                            <span>{totalScore}</span>
                          ) : (
                            <div className="h-4 w-4 bg-blue-500 rounded-full animate-pulse"></div>
                          )}
                        </Text>
                      </div>
                    </div>
                  </div>

                  {/* Criteria in horizontal scroll */}
                  <div className="mb-4">
                    <Text
                      variant="p"
                      className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2"
                    >
                      {t("assistant.essayGrader.criteria") || "Kriterler"}
                    </Text>
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 overflow-x-auto pb-2">
                      {criteriaEvaluations.map((criterion) => {
                        const essayCriterion =
                          essay.result?.criteria_evaluations?.find(
                            (c) =>
                              c.criterion_name === criterion.criterion_name,
                          );
                        return (
                          <div
                            key={criterion.criterion_name}
                            className="flex-shrink-0 bg-gray-50 dark:bg-gray-800 rounded-lg p-3 min-w-[120px] text-center"
                          >
                            <Text
                              variant="span"
                              className="text-xs text-gray-500 dark:text-gray-400 block truncate"
                              title={criterion.criterion_name}
                            >
                              {criterion.criterion_name}
                            </Text>
                            <Text
                              variant="span"
                              className="text-sm font-medium text-gray-700 dark:text-gray-300 block mt-1"
                            >
                              {essayCriterion?.score !== undefined ? (
                                essayCriterion.score
                              ) : (
                                <div className="h-4 w-4 bg-blue-500 rounded-full animate-pulse mx-auto"></div>
                              )}
                            </Text>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button
                      onClick={() => {
                        if (!essay.result) {
                          addToast({
                            title:
                              t("assistant.essayGrader.noResult") ||
                              "Sonuç Yok",
                            description:
                              t("assistant.essayGrader.noResultDescription") ||
                              "Henüz analiz sonucu bulunmuyor",
                            variant: "info",
                            duration: 5000,
                          });
                          return;
                        }
                        setSelectedEssayIndex(index);
                        setView("details");
                        setActiveSection("details");
                      }}
                      size="sm"
                      rounded="lg"
                      bgColor="bg-blue-600 hover:bg-blue-700"
                      textColor="text-white"
                      className="flex items-center flex-1"
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      {t("assistant.essayGrader.overview") || "Genel Bakış"}
                    </Button>
                    <Button
                      onClick={() => {
                        if (
                          !essay.result ||
                          !essay.messages ||
                          essay.messages.length === 0
                        ) {
                          addToast({
                            title:
                              t("assistant.essayGrader.noResult") ||
                              "Sonuç Yok",
                            description:
                              t("assistant.essayGrader.noResultDescription") ||
                              "Henüz analiz sonucu bulunmuyor",
                            variant: "info",
                            duration: 5000,
                          });
                          return;
                        }

                        setUploadedFiles([]);
                        handleSelectChat(Number(newChatId), essay.id);
                      }}
                      size="sm"
                      rounded="lg"
                      bgColor="bg-gray-600 hover:bg-gray-700"
                      textColor="text-white"
                      className="flex items-center flex-1"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      {t("assistant.essayGrader.chatPage") || "Sohbet"}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Mobile View - Compact card layout */}
        <div className="md:hidden space-y-3">
          {results.map((essay, index) => {
            const totalScore = getTotalScore(essay.result);

            return (
              <div
                key={essay.id}
                className={`bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden ${
                  selectedEssayIndex === index
                    ? "border-blue-300 dark:border-blue-600"
                    : ""
                }`}
              >
                {/* Header */}
                <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex justify-between items-center">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate flex-1">
                      {essay.essayFiles?.[0]?.fileName ||
                        `Kompozisyon ${index + 1}`}
                    </h3>
                    <div className="ml-2 bg-blue-50 dark:bg-blue-900/20 px-2 py-1 rounded-md">
                      <Text
                        variant="span"
                        className="text-xs font-bold text-blue-700 dark:text-blue-300"
                      >
                        {totalScore !== null &&
                        typeof totalScore === "number" ? (
                          <span>{totalScore}</span>
                        ) : (
                          <div className="h-3 w-3 bg-blue-500 rounded-full animate-pulse"></div>
                        )}
                      </Text>
                    </div>
                  </div>
                </div>

                {/* Criteria - Horizontal scroll */}
                <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                  <div className="grid grid-cols-3 lg:grid-cols-4 gap-2 overflow-x-auto pb-1">
                    {criteriaEvaluations.map((criterion) => {
                      const essayCriterion =
                        essay.result?.criteria_evaluations?.find(
                          (c) => c.criterion_name === criterion.criterion_name,
                        );
                      return (
                        <div
                          key={criterion.criterion_name}
                          className="flex-shrink-0 bg-gray-50 dark:bg-gray-800 rounded-md p-2 min-w-[80px] text-center"
                        >
                          <Text
                            variant="span"
                            className="text-xs text-gray-500 dark:text-gray-400 block truncate"
                            title={criterion.criterion_name || ""}
                          >
                            {criterion.criterion_name &&
                            criterion.criterion_name.length > 8
                              ? criterion.criterion_name.substring(0, 8) + "..."
                              : criterion.criterion_name || ""}
                          </Text>
                          <Text
                            variant="span"
                            className="text-sm font-medium text-gray-700 dark:text-gray-300 block"
                          >
                            {essayCriterion?.score !== undefined ? (
                              essayCriterion.score
                            ) : (
                              <div className="h-3 w-3 bg-blue-500 rounded-full animate-pulse mx-auto"></div>
                            )}
                          </Text>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Actions */}
                <div className="p-3">
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      onClick={() => {
                        if (!essay.result) {
                          addToast({
                            title:
                              t("assistant.essayGrader.noResult") ||
                              "Sonuç Yok",
                            description:
                              t("assistant.essayGrader.noResultDescription") ||
                              "Henüz analiz sonucu bulunmuyor",
                            variant: "info",
                            duration: 5000,
                          });
                          return;
                        }
                        setSelectedEssayIndex(index);
                        setView("details");
                        setActiveSection("details");
                      }}
                      size="sm"
                      rounded="lg"
                      bgColor="bg-blue-600 hover:bg-blue-700"
                      textColor="text-white"
                      className="flex items-center justify-center"
                    >
                      <Eye className="h-3 w-3 mr-1" />
                      <span className="text-xs">Görüntüle</span>
                    </Button>
                    <Button
                      onClick={() => {
                        if (
                          !essay.result ||
                          !essay.messages ||
                          essay.messages.length === 0
                        ) {
                          addToast({
                            title:
                              t("assistant.essayGrader.noResult") ||
                              "Sonuç Yok",
                            description:
                              t("assistant.essayGrader.noResultDescription") ||
                              "Henüz analiz sonucu bulunmuyor",
                            variant: "info",
                            duration: 5000,
                          });
                          return;
                        }

                        setUploadedFiles([]);
                        handleSelectChat(Number(newChatId), essay.id);
                      }}
                      size="sm"
                      rounded="lg"
                      bgColor="bg-gray-600 hover:bg-gray-700"
                      textColor="text-white"
                      className="flex items-center justify-center"
                    >
                      <ExternalLink className="h-3 w-3 mr-1" />
                      <span className="text-xs">Sohbet</span>
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Render assignments section
  const renderAssignmentsSection = () => {
    if (!essayAssignments) {
      return (
        <div className="mt-6 md:mt-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="h-5 w-5 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              <div className="h-6 w-48 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            </div>
          </div>

          <div className="space-y-4">
            {[1, 2, 3].map((index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4"
              >
                <div>
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <div className="h-6 w-64 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-2" />

                      <div className="flex flex-wrap items-center gap-2 md:gap-4 mb-2">
                        <div className="flex items-center gap-1">
                          <div className="h-3 w-3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                          <div className="h-3 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="h-3 w-3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                          <div className="h-3 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-20 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
                    {[1, 2, 3, 4].map((statIndex) => (
                      <div
                        key={statIndex}
                        className="text-center p-2 md:p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                      >
                        <div className="h-4 md:h-6 w-6 md:w-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mx-auto mb-1 md:mb-2" />
                        <div className="h-2 md:h-3 w-12 md:w-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mx-auto" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (
      !essayAssignments.assignments ||
      essayAssignments.assignments.length === 0
    ) {
      return (
        <div className="mt-6 md:mt-8 text-center text-gray-500 dark:text-gray-400">
          <Text variant="h3" className="mb-2 text-base md:text-lg">
            {t("essayAssignments.noAssignments") || "Henüz kompozisyon yok"}
          </Text>
          <Text variant="p" status="secondary" className="text-sm md:text-base">
            {t("essayAssignments.noAssignmentsDescription") ||
              "Henüz kompozisyon ödeviniz bulunmuyor"}
          </Text>
        </div>
      );
    }

    return (
      <div className="mt-6 md:mt-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 md:h-5 md:w-5 text-blue-500" />
            <Text
              variant="h2"
              size="lg"
              className="font-semibold text-sm md:text-xl"
            >
              {t("essayAssignments.title") || "Kompozisyon Ödevleri"}
            </Text>
          </div>
        </div>

        <div className="space-y-3 md:space-y-4">
          {essayAssignments.assignments.map((assignment) => {
            return (
              <div
                key={assignment.id}
                className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 md:p-4 transition-all"
              >
                <div>
                  <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-2 md:gap-0 mb-2">
                    <div className="flex-1 min-w-0">
                      <Text
                        variant="h3"
                        size="base"
                        className="font-medium mb-1 truncate md:text-lg"
                      >
                        {assignment.title}
                      </Text>

                      <div className="flex flex-wrap items-center gap-2 md:gap-4 text-xs text-gray-500 dark:text-gray-400 mb-2">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          <span className="whitespace-nowrap">
                            {t("essayAssignments.createdAt") || "Oluşturulma"}:{" "}
                            {assignment.createdAt
                              ? formatShortDate(assignment.createdAt)
                              : "-"}
                          </span>
                        </span>
                        {assignment.dueDate && (
                          <span className="flex items-center gap-1">
                            <CalendarDays className="h-3 w-3" />
                            <span className="whitespace-nowrap">
                              {t("essayAssignments.dueDate") || "Teslim Tarihi"}
                              : {formatShortDate(assignment.dueDate)}
                            </span>
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 md:mt-0">
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/essay-assignments/${assignment.id}`);
                        }}
                        size="sm"
                        rounded="lg"
                        bgColor="bg-blue-600 hover:bg-blue-700"
                        textColor="text-white"
                        className="flex items-center text-xs md:text-sm"
                      >
                        <Eye className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                        {t("essayAssignments.actions.view") || "Görüntüle"}
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
                    <div className="text-center p-2 md:p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <div className="text-base md:text-lg font-semibold text-blue-600 dark:text-blue-400">
                        {assignment.totalStudents}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {t("essayAssignments.stats.totalStudents") ||
                          "Toplam Öğrenci"}
                      </div>
                    </div>
                    <div className="text-center p-2 md:p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <div className="text-base md:text-lg font-semibold text-green-600 dark:text-green-400">
                        {assignment.submittedCount}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {t("essayAssignments.stats.submitted") ||
                          "Teslim Edildi"}
                      </div>
                    </div>
                    <div className="text-center p-2 md:p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                      <div className="text-base md:text-lg font-semibold text-yellow-600 dark:text-yellow-400">
                        {assignment.pendingCount}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {t("essayAssignments.stats.pending") || "Beklemede"}
                      </div>
                    </div>
                    <div className="text-center p-2 md:p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                      <div className="text-base md:text-lg font-semibold text-purple-600 dark:text-purple-400">
                        {assignment.gradedCount}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {t("essayAssignments.stats.graded") ||
                          "Değerlendirildi"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Assignment Setup and Students views
  if (view === "assignment-setup" || view === "assignment-students") {
    return (
      <div className="flex-1 md:p-8 p-4 lg:mt-5 bg-gray-50 dark:bg-dark-500 lg:rounded-tl-3xl overflow-y-auto h-full custom-scrollbar">
        <div className="max-w-7xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key="assignment"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 md:p-6"
            >
              {view === "assignment-setup" && (
                <div className="space-y-4 md:space-y-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <Text
                      variant="h2"
                      size="lg"
                      className="font-semibold md:text-xl"
                    >
                      {t("assistant.essayGrader.createAssignment") ||
                        "Kompozisyon Ödevi Oluştur"}
                    </Text>
                    <Button
                      onClick={handleReset}
                      className="flex items-center self-start"
                      bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
                      borderWidth={"thin"}
                      borderColor="border-gray-200 dark:border-gray-700"
                      textColor="text-gray-700 dark:text-gray-200"
                      size="sm"
                      rounded="lg"
                    >
                      <ChevronLeft className="h-4 w-4 mr-2" />
                      {t("common.back") || "Geri"}
                    </Button>
                  </div>

                  {/* Assignment Title */}
                  <div className="space-y-3 md:space-y-4">
                    <Text
                      variant="h4"
                      className="text-gray-700 dark:text-gray-300 text-sm font-medium"
                    >
                      {t("assistant.essayGrader.assignmentTitle") ||
                        "Kompozisyon Başlığı"}
                    </Text>
                    <input
                      type="text"
                      value={assignmentTitle}
                      onChange={(e) => setAssignmentTitle(e.target.value)}
                      placeholder={
                        t("assistant.essayGrader.assignmentTitlePlaceholder") ||
                        "Kompozisyon başlığı girin..."
                      }
                      className="w-full p-3 rounded-xl border border-gray-200 dark:border-dark-400 bg-white dark:bg-dark-600 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm md:text-base"
                    />
                  </div>

                  {/* Due Date and Time */}
                  <div className="space-y-3 md:space-y-4">
                    <Text
                      variant="h4"
                      className="text-gray-700 dark:text-gray-300 text-sm font-medium"
                    >
                      {t("assistant.essayGrader.dueDate") || "Teslim Tarihi"}
                    </Text>
                    <DateTimePicker
                      value={dueDate}
                      onChange={setDueDate}
                      minDate={new Date()}
                      className="w-full"
                      placeholder={
                        t("assistant.essayGrader.selectDueDate") ||
                        "Teslim tarihi seçin"
                      }
                    />
                  </div>

                  {/* Input Type Selection */}
                  <div className="space-y-3 md:space-y-4">
                    <Text variant="h3" size="base" className="font-medium">
                      {t("assistant.essayGrader.selectInputType")}
                    </Text>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                      <Button
                        onClick={() => setAssignmentMode("TEXT")}
                        rounded="xl"
                        bgColor={`${
                          assignmentMode === "TEXT" &&
                          "bg-blue-50 dark:bg-blue-900/20"
                        }`}
                        className={`p-4 border-2 ${
                          assignmentMode === "TEXT"
                            ? "border-blue-500"
                            : "border-gray-200 hover:border-blue-400"
                        }`}
                        fullWidth
                      >
                        <div className="flex items-center justify-center space-x-2">
                          <Sparkles className="w-4 h-4 md:w-5 md:h-5" />
                          <span className="text-sm md:text-base">
                            {t("assistant.essayGrader.textInput") || "Metin"}
                          </span>
                        </div>
                      </Button>

                      <Button
                        onClick={() => setAssignmentMode("PDF")}
                        className={`p-4 border-2 ${
                          assignmentMode === "PDF"
                            ? "border-blue-500"
                            : "border-gray-200 hover:border-blue-400"
                        }`}
                        bgColor={`${
                          assignmentMode === "PDF" &&
                          "bg-blue-50 dark:bg-blue-900/20"
                        }`}
                        rounded="xl"
                        fullWidth
                      >
                        <div className="flex items-center justify-center space-x-2">
                          <FileText className="w-4 h-4 md:w-5 md:h-5" />
                          <span className="text-sm md:text-base">
                            {t("assistant.essayGrader.file") || "Dosya"}
                          </span>
                        </div>
                      </Button>
                    </div>

                    {assignmentMode === "TEXT" ? (
                      <div className="space-y-3 md:space-y-4">
                        <Text
                          variant="h4"
                          className="text-gray-700 dark:text-gray-300 text-sm font-medium"
                        >
                          {t("assistant.essayGrader.assignmentPrompt") ||
                            "Kompozisyon Metni"}
                        </Text>
                        <textarea
                          value={assignmentTopic}
                          onChange={(e) => setAssignmentTopic(e.target.value)}
                          placeholder={
                            t(
                              "assistant.essayGrader.assignmentPromptPlaceholder",
                            ) || "Kompozisyon metnini girin..."
                          }
                          className="w-full p-3 rounded-xl border border-gray-200 dark:border-dark-400 bg-white dark:bg-dark-600 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm md:text-base"
                          rows={4}
                        />
                      </div>
                    ) : (
                      <div className="space-y-3 md:space-y-4">
                        <Text
                          variant="h4"
                          className="text-gray-700 dark:text-gray-300 text-sm font-medium"
                        >
                          {t("assistant.essayGrader.assignmentPrompt") ||
                            "Kompozisyon Metni"}
                        </Text>
                        <FileUploader
                          accept={acceptedFileTypes}
                          onUploadStart={() => {
                            setActiveUploads((prev) => prev + 1);
                          }}
                          onUploadSuccess={(url, response) => {
                            if (response?.id) {
                              handleFileUploadSuccess("assignment-instruction")(
                                url,
                                response,
                              );
                              setAssignmentTopic(
                                response.fileName?.replace(/\.[^/.]+$/, "") ??
                                  "",
                              );
                            }
                          }}
                          onUploadError={(error) => {
                            handleFileUploadError(error);
                          }}
                          onFileDelete={() => {
                            handleFileDelete("assignment-instruction");
                          }}
                          dragActiveText={
                            t("assistant.essayGrader.dropRubric") ||
                            "Dosyayı buraya bırakın"
                          }
                          dragInactiveText={
                            t("assistant.essayGrader.uploadRubric") ||
                            "Dosya yüklemek için tıklayın"
                          }
                          uploadColor="blue"
                          extractColor="blue"
                          successColor="green"
                          shouldExtractContent
                          multiple={false}
                        />
                      </div>
                    )}
                  </div>

                  {/* Grading Criteria Upload */}
                  <div className="space-y-3 md:space-y-4">
                    <Text
                      variant="h4"
                      className="text-gray-700 dark:text-gray-300 text-sm font-medium"
                    >
                      {t("assistant.essayGrader.uploadGradingCriteriaFile")}
                    </Text>
                    <FileUploader
                      accept={acceptedFileTypes}
                      onUploadStart={() => {
                        setActiveUploads((prev) => prev + 1);
                      }}
                      onUploadSuccess={(url, response) => {
                        handleFileUploadSuccess("rubric")(url, response);
                      }}
                      onUploadError={(error) => {
                        handleFileUploadError(error);
                      }}
                      onFileDelete={() => {
                        handleFileDelete("rubric");
                      }}
                      dragActiveText={
                        t("assistant.essayGrader.dropRubric") ||
                        "Dosyayı buraya bırakın"
                      }
                      dragInactiveText={
                        t("assistant.essayGrader.uploadRubric") ||
                        "Dosya yüklemek için tıklayın"
                      }
                      uploadColor="blue"
                      extractColor="blue"
                      successColor="green"
                      shouldExtractContent
                      multiple={false}
                    />
                  </div>

                  {/* Tolerance Level Selector */}
                  <div>
                    <ToleranceLevelSelector
                      value={toleranceLevel}
                      onChange={setToleranceLevel}
                    />
                  </div>

                  <Button
                    variant="solid"
                    size="lg"
                    rounded="xl"
                    fullWidth
                    bgColor="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                    textColor="text-white"
                    className="shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 focus:ring-4 focus:ring-blue-500/50"
                    disabled={!canStartAnalysis}
                    onClick={handleCreateAssignment}
                  >
                    {t("assistant.essayGrader.assignToStudents") ||
                      "Öğrenciye Ata"}
                  </Button>
                </div>
              )}

              {view === "assignment-students" && (
                <div className="space-y-4 md:space-y-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <Text
                      variant="h2"
                      size="lg"
                      className="font-semibold md:text-xl"
                    >
                      {t("assistant.essayGrader.selectStudents") ||
                        "Öğrenci Seç"}
                    </Text>
                    <Button
                      onClick={() => setView("assignment-setup")}
                      className="flex items-center self-start"
                      bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
                      borderWidth={"thin"}
                      borderColor="border-gray-200 dark:border-gray-700"
                      textColor="text-gray-700 dark:text-gray-200"
                      size="sm"
                      rounded="lg"
                    >
                      <ChevronLeft className="h-4 w-4 mr-2" />
                      {t("common.back") || "Geri"}
                    </Button>
                  </div>

                  <ClassroomAndStudentSelector
                    selectedStudents={selectedStudents}
                    setSelectedStudents={setSelectedStudents}
                    assignedStudentIds={assignedStudentIds}
                    shouldUseModal={false}
                  />

                  <Button
                    variant="solid"
                    size="lg"
                    rounded="xl"
                    fullWidth
                    bgColor="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                    textColor="text-white"
                    className="shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 focus:ring-4 focus:ring-green-500/50"
                    disabled={
                      selectedStudents.length === 0 || isCreatingAssignment
                    }
                    onClick={handleAssignToStudents}
                  >
                    {isCreatingAssignment ? (
                      <div className="flex items-center justify-center">
                        <Loader2 className="h-5 w-5 animate-spin mr-2" />
                        {t("assistant.essayGrader.creatingAssignment") ||
                          "Kompozisyon oluşturuluyor..."}
                      </div>
                    ) : (
                      <div className="flex items-center justify-center">
                        <UserPlus className="h-5 w-5 mr-2" />
                        {t("assistant.essayGrader.assignToSelectedStudents") ||
                          "Seçili Öğrencilere Ata"}
                      </div>
                    )}
                  </Button>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    );
  }

  // Main component views
  return (
    <div className="flex-1 md:p-8 p-4 lg:mt-5 bg-gray-50 dark:bg-dark-500 lg:rounded-tl-3xl overflow-y-auto h-full custom-scrollbar">
      <div className="max-w-7xl mx-auto">
        {/* Header Section - Hidden on mobile */}
        <div className="hidden lg:block mb-6">
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
            <div>
              <Text variant="h1" size="3xl" className="font-bold">
                {t("assistant.essayGrader.uploadTitle")}
              </Text>
              <Text variant="p" status="secondary" className="mt-2">
                {t("assistant.essayGrader.description")}
              </Text>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={handleClickHistory}
                className="flex items-center"
                bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
                borderWidth={"thin"}
                borderColor="border-gray-200 dark:border-gray-700"
                textColor="text-gray-700 dark:text-gray-200"
                size="md"
                rounded="lg"
              >
                <Clock className="h-4 w-4 mr-2" />
                {t("assistant.chatHistory") || "Geçmiş"}
              </Button>

              {view !== "upload" && (
                <Button
                  onClick={handleReset}
                  className="flex items-center"
                  bgColor="bg-blue-600 hover:bg-blue-700"
                  textColor="text-white"
                  size="md"
                  rounded="lg"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {t("assistant.essayGrader.newAnalysis") || "Yeni Analiz"}
                </Button>
              )}
            </div>
          </div>

          {/* Progress steps */}
          {view !== "upload" && (
            <div className="mt-8">
              <div className="flex flex-wrap items-center gap-2 md:gap-0">
                <div
                  className={`flex items-center py-2 px-4 rounded-r-lg ${
                    activeSection === "results" || activeSection === "details"
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400"
                  }`}
                >
                  <span
                    className={`w-6 h-6 flex items-center justify-center rounded-full text-sm font-medium mr-2 ${
                      activeSection === "results" || activeSection === "details"
                        ? "bg-white text-blue-600"
                        : "bg-gray-300 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                    }`}
                  >
                    3
                  </span>
                  <span className="text-sm font-medium">
                    {t("assistant.essayGrader.results") || "Sonuçlar"}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Main Content with AnimatePresence */}
        <AnimatePresence mode="wait">
          {/* Upload Section */}
          {view === "upload" && (
            <motion.div
              key="upload"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-6 md:space-y-8"
            >
              {/* Beautiful Step Header */}
              <div className="text-center hidden">
                <div className="inline-flex items-center gap-3 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-full px-4 md:px-6 py-3 border border-blue-200 dark:border-blue-800">
                  <div className="w-6 h-6 md:w-8 md:h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-semibold">
                    1
                  </div>
                  <Text
                    variant="h3"
                    size="base"
                    className="font-semibold text-gray-800 dark:text-gray-200 md:text-lg"
                  >
                    {t("assistant.essayGrader.upload")}
                  </Text>
                </div>
              </div>

              {!gradingMode ? (
                /* Mode Selection */
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button
                    onClick={() => setGradingMode("self")}
                    className={`
                      relative w-full p-8 rounded-2xl border border-gray-200 dark:border-dark-400
                      bg-white dark:bg-dark-900 hover:bg-gray-50 dark:hover:bg-dark-600 
                      transition-all duration-300 ease-out
                      group overflow-hidden cursor-pointer
                      shadow-sm hover:shadow-lg
                    `}
                  >
                    {/* Content */}
                    <div className="relative z-10 flex items-start space-x-6">
                      {/* Logo/Icon */}
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 rounded-2xl bg-gradient-to-r from-blue-500 to-indigo-600 flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow duration-300">
                          <Sparkles className="w-8 h-8 text-white" />
                        </div>
                      </div>

                      {/* Text content */}
                      <div className="flex-1 min-w-0 text-left">
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-1 leading-tight text-left">
                          {t("assistant.essayGrader.evaluateOwn") ||
                            "Kendi Kompozisyonumu Değerlendir"}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-300 text-base leading-relaxed text-left">
                          {t("assistant.essayGrader.evaluateOwnDescription") ||
                            "Kendi kompozisyonunuzu yükleyerek AI ile değerlendirin"}
                        </p>
                      </div>

                      {/* Arrow indicator */}
                      <div className="flex-shrink-0 mt-1">
                        <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-dark-400 flex items-center justify-center transition-all duration-300 ease-out">
                          <svg
                            className="w-4 h-4 text-gray-600 dark:text-gray-300 transition-transform duration-300"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* Subtle glow effect */}
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500/0 via-indigo-500/0 to-purple-500/0 transition-all duration-300" />
                  </button>

                  <button
                    onClick={() => {
                      setGradingMode("assignment");
                      setView("assignment-setup");
                      setActiveSection("assignment-setup");
                    }}
                    className={`
                      relative w-full p-8 rounded-2xl border border-gray-200 dark:border-dark-400
                      bg-white dark:bg-dark-900 hover:bg-gray-50 dark:hover:bg-dark-600 
                      transition-all duration-300 ease-out
                      group overflow-hidden cursor-pointer
                      shadow-sm hover:shadow-lg
                    `}
                  >
                    {/* Content */}
                    <div className="relative z-10 flex items-start space-x-6">
                      {/* Logo/Icon */}
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 rounded-2xl bg-gradient-to-r from-green-500 to-emerald-600 flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow duration-300">
                          <UserPlus className="w-8 h-8 text-white" />
                        </div>
                      </div>

                      {/* Text content */}
                      <div className="flex-1 min-w-0 text-left">
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-1 leading-tight text-left">
                          {t("assistant.essayGrader.assignToStudents") ||
                            "Öğrenciye Ata"}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-300 text-base leading-relaxed text-left">
                          {t(
                            "assistant.essayGrader.assignToStudentsDescription",
                          ) ||
                            "Öğrencilere kompozisyon ödevi oluşturun ve atayın"}
                        </p>
                      </div>

                      {/* Arrow indicator */}
                      <div className="flex-shrink-0 mt-1">
                        <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-dark-400 flex items-center justify-center transition-all duration-300 ease-out">
                          <svg
                            className="w-4 h-4 text-gray-600 dark:text-gray-300 transition-transform duration-300"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* Subtle glow effect */}
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-green-500/0 via-emerald-500/0 to-teal-500/0 transition-all duration-300" />
                  </button>
                </div>
              ) : (
                <>
                  {/* Back Button for Self-Grading Mode */}
                  {gradingMode === "self" && (
                    <div className="flex justify-start">
                      <Button
                        onClick={() => {
                          setGradingMode(null);
                          setUploadedFiles([]);
                          setUploadedEssayCount(0);
                          setActiveUploads(0);
                          setInstructionsIncludedInRubric(false);
                        }}
                        className="flex items-center"
                        bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
                        borderWidth={"thin"}
                        borderColor="border-gray-200 dark:border-gray-700"
                        textColor="text-gray-700 dark:text-gray-200"
                        size="sm"
                        rounded="lg"
                      >
                        <ChevronLeft className="h-4 w-4 mr-2" />
                        {t("common.back") || "Geri"}
                      </Button>
                    </div>
                  )}

                  {/* Main Upload Cards */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
                    {/* Rubric Card */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 }}
                      className="relative group order-1"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl blur-lg opacity-20 group-hover:opacity-30 transition-opacity duration-300"></div>
                      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-4 md:p-6 hover:shadow-2xl transition-all duration-300">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg">
                            <FileText className="h-5 w-5 md:h-6 md:w-6 text-white" />
                          </div>
                          <div>
                            <Text
                              variant="h3"
                              size="lg"
                              className="font-bold text-gray-800 dark:text-white md:text-xl"
                            >
                              {t("assistant.essayGrader.rubric")}
                            </Text>
                            <Text
                              variant="p"
                              size="sm"
                              status="secondary"
                              className="text-gray-500 dark:text-gray-400"
                            >
                              {t("assistant.essayGrader.rubricDescription")}
                            </Text>
                          </div>
                        </div>

                        <FileUploader
                          accept={acceptedFileTypes}
                          onUploadStart={() => {
                            setActiveUploads((prev) => prev + 1);
                          }}
                          onUploadSuccess={(url, response) => {
                            handleFileUploadSuccess("rubric")(url, response);
                          }}
                          onUploadError={(error) => {
                            handleFileUploadError(error);
                          }}
                          onFileDelete={() => {
                            setUploadedFiles(
                              uploadedFiles.filter((f) => f.type !== "rubric"),
                            );
                          }}
                          dragActiveText={t("assistant.essayGrader.dropRubric")}
                          dragInactiveText={t(
                            "assistant.essayGrader.uploadRubric",
                          )}
                          uploadColor="blue"
                          extractColor="blue"
                          successColor="green"
                          shouldExtractContent
                          multiple={false}
                        />
                      </div>
                    </motion.div>

                    {/* Instructions Card */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                      className="relative group order-2"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-orange-500 to-amber-600 rounded-2xl blur-lg opacity-20 group-hover:opacity-30 transition-opacity duration-300"></div>
                      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-4 md:p-6 hover:shadow-2xl transition-all duration-300">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gradient-to-r from-orange-500 to-amber-600 flex items-center justify-center shadow-lg">
                            <BookOpen className="h-4 w-4 md:h-5 md:w-5 text-white" />
                          </div>
                          <div>
                            <Text
                              variant="h3"
                              size="lg"
                              className="font-bold text-gray-800 dark:text-white md:text-xl"
                            >
                              {t("assistant.essayGrader.instructions.title")}
                            </Text>
                            <Text
                              variant="p"
                              size="sm"
                              status="secondary"
                              className="text-gray-500 dark:text-gray-400"
                            >
                              {t(
                                "assistant.essayGrader.instructions.description",
                              )}
                            </Text>
                          </div>
                        </div>

                        {/* Beautiful Toggle Section */}
                        <div className="mb-4 md:mb-6 p-3 md:p-4 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 rounded-xl border border-gray-200 dark:border-gray-600">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <Text
                                  variant="h4"
                                  size="sm"
                                  className="font-semibold text-gray-900 dark:text-gray-100"
                                >
                                  {t(
                                    "assistant.essayGrader.instructions.included",
                                  )}
                                </Text>
                                <div className="relative">
                                  <button
                                    onClick={() =>
                                      setShowInstructionsInfo(
                                        !showInstructionsInfo,
                                      )
                                    }
                                    className="p-1 rounded-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors duration-200"
                                  >
                                    <Info className="h-3 w-3 text-gray-600 dark:text-gray-400" />
                                  </button>
                                  {showInstructionsInfo && (
                                    <div className="instructions-info-tooltip absolute left-0 top-8 z-50 w-72 md:w-80 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-600">
                                      <div className="space-y-2">
                                        <Text
                                          variant="p"
                                          size="sm"
                                          className="font-medium text-gray-900 dark:text-gray-100 mb-3"
                                        >
                                          {t(
                                            "assistant.essayGrader.instructions.tooltip.title",
                                          )}
                                        </Text>
                                        <div className="space-y-2">
                                          <div className="flex items-start gap-2">
                                            <div className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2 flex-shrink-0"></div>
                                            <Text
                                              variant="p"
                                              size="xs"
                                              className="text-gray-700 dark:text-gray-300"
                                            >
                                              {t(
                                                "assistant.essayGrader.instructions.tooltip.point1",
                                              )}
                                            </Text>
                                          </div>
                                          <div className="flex items-start gap-2">
                                            <div className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2 flex-shrink-0"></div>
                                            <Text
                                              variant="p"
                                              size="xs"
                                              className="text-gray-700 dark:text-gray-300"
                                            >
                                              {t(
                                                "assistant.essayGrader.instructions.tooltip.point2",
                                              )}
                                            </Text>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="ml-4">
                              <button
                                onClick={() =>
                                  setInstructionsIncludedInRubric(
                                    !instructionsIncludedInRubric,
                                  )
                                }
                                className={`relative inline-flex h-6 w-11 md:h-8 md:w-14 items-center rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 shadow-lg transform hover:scale-105 ${
                                  instructionsIncludedInRubric
                                    ? "bg-gradient-to-r from-orange-500 to-amber-500 shadow-orange-200 dark:shadow-orange-900"
                                    : "bg-gradient-to-r from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-500"
                                }`}
                              >
                                <span
                                  className={`inline-block h-4 w-4 md:h-6 md:w-6 transform rounded-full bg-white transition-all duration-300 shadow-md ${
                                    instructionsIncludedInRubric
                                      ? "translate-x-6 md:translate-x-7"
                                      : "translate-x-1"
                                  }`}
                                />
                                <div
                                  className={`absolute inset-0 rounded-full transition-all duration-300 ${
                                    instructionsIncludedInRubric
                                      ? "ring-2 ring-orange-300 dark:ring-orange-600"
                                      : ""
                                  }`}
                                />
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Dynamic Content Area */}
                        <div className="min-h-[180px] md:min-h-[200px]">
                          <AnimatePresence mode="wait">
                            {!instructionsIncludedInRubric ? (
                              <motion.div
                                key="upload"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.3 }}
                                className="space-y-4"
                              >
                                <div className="flex items-center gap-2">
                                  <div className="w-3 h-3 rounded-full bg-gradient-to-r from-orange-500 to-amber-500"></div>
                                  <Text
                                    variant="span"
                                    size="sm"
                                    className="font-medium text-orange-600 dark:text-orange-400"
                                  >
                                    Ayrı dosya yükleyin
                                  </Text>
                                </div>
                                <FileUploader
                                  accept={acceptedFileTypes}
                                  onUploadStart={() => {
                                    setActiveUploads((prev) => prev + 1);
                                  }}
                                  onUploadSuccess={(url, response) => {
                                    handleFileUploadSuccess("instructions")(
                                      url,
                                      response,
                                    );
                                  }}
                                  onUploadError={(error) => {
                                    handleFileUploadError(error);
                                  }}
                                  onFileDelete={() => {
                                    setUploadedFiles(
                                      uploadedFiles.filter(
                                        (f) => f.type !== "instructions",
                                      ),
                                    );
                                  }}
                                  dragActiveText={t(
                                    "assistant.essayGrader.instructions.dropInstructions",
                                  )}
                                  dragInactiveText={t(
                                    "assistant.essayGrader.instructions.uploadInstructions",
                                  )}
                                  uploadColor="orange"
                                  extractColor="orange"
                                  successColor="green"
                                  shouldExtractContent
                                  multiple={false}
                                />
                              </motion.div>
                            ) : (
                              <motion.div
                                key="included"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.3 }}
                                className="flex items-center justify-center h-full"
                              >
                                <div className="text-center p-6 md:p-8 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl border-2 border-dashed border-green-300 dark:border-green-700">
                                  <div className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center shadow-lg">
                                    <CheckCircle className="h-6 w-6 md:h-8 md:w-8 text-white" />
                                  </div>
                                  <Text
                                    variant="h4"
                                    className="font-semibold text-green-700 dark:text-green-300 mb-2"
                                  >
                                    ✅ Hazır
                                  </Text>
                                  <Text
                                    variant="p"
                                    size="sm"
                                    className="text-green-600 dark:text-green-400"
                                  >
                                    Talimatlar rubrikte mevcut
                                  </Text>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      </div>
                    </motion.div>

                    {/* Essays Card */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                      className="relative group order-3"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl blur-lg opacity-20 group-hover:opacity-30 transition-opacity duration-300"></div>
                      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-4 md:p-6 hover:shadow-2xl transition-all duration-300">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gradient-to-r from-green-500 to-emerald-600 flex items-center justify-center shadow-lg">
                            <FileText className="h-5 w-5 md:h-6 md:w-6 text-white" />
                          </div>
                          <div>
                            <Text
                              variant="h3"
                              size="lg"
                              className="font-bold text-gray-800 dark:text-white md:text-xl"
                            >
                              {t("assistant.essayGrader.essays")}
                            </Text>
                            <Text
                              variant="p"
                              size="sm"
                              status="secondary"
                              className="text-gray-500 dark:text-gray-400"
                            >
                              {t("assistant.essayGrader.essaysDescription")}
                            </Text>
                          </div>
                        </div>

                        <FileUploader
                          accept={acceptedFileTypes}
                          onUploadStart={() => {
                            setActiveUploads((prev) => prev + 1);
                          }}
                          onUploadSuccess={(url, response) => {
                            handleFileUploadSuccess("essay")(url, response);
                          }}
                          onAllUploadsComplete={(files) => {
                            console.log("All files have been uploaded:", files);
                            setUploadedEssayCount(files.length);
                            setActiveUploads(0);
                          }}
                          onUploadError={(error) => {
                            handleFileUploadError(error);
                          }}
                          onFileDelete={(fileId) => {
                            setUploadedFiles((prev) =>
                              prev.filter((a) => a.fileId !== fileId),
                            );
                          }}
                          dragActiveText={t("assistant.essayGrader.dropEssay")}
                          dragInactiveText={t(
                            "assistant.essayGrader.uploadEssay",
                          )}
                          uploadColor="green"
                          extractColor="green"
                          successColor="green"
                          shouldExtractContent
                          multiple={true}
                        />
                      </div>
                    </motion.div>
                  </div>
                </>
              )}

              {/* Tolerance Level Selector */}
              {gradingMode === "self" && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="relative"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-600 rounded-2xl blur-lg opacity-10"></div>
                  <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-6 md:p-8">
                    <ToleranceLevelSelector
                      value={toleranceLevel}
                      onChange={setToleranceLevel}
                    />
                  </div>
                </motion.div>
              )}

              {/* Status and Actions */}
              {gradingMode === "self" && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="relative"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl blur-lg opacity-10"></div>
                  <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-4 md:p-6">
                    {uploadedFiles.length === 0 ? (
                      <div className="flex items-center justify-center space-x-3 p-4 md:p-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                        <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 flex items-center justify-center">
                          <AlertCircle className="h-4 w-4 md:h-5 md:w-5 text-white" />
                        </div>
                        <Text
                          variant="p"
                          className="text-blue-800 dark:text-blue-200 font-medium text-sm md:text-base text-center"
                        >
                          {t("assistant.essayGrader.uploadBothFiles")}
                        </Text>
                      </div>
                    ) : (
                      <div className="flex flex-col lg:flex-row items-center justify-between gap-4 md:gap-6">
                        <div className="flex items-center gap-3 w-full lg:w-auto">
                          <div
                            className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center ${
                              canStartAnalysis
                                ? "bg-gradient-to-r from-green-500 to-emerald-600"
                                : "bg-gradient-to-r from-amber-500 to-orange-600"
                            }`}
                          >
                            {canStartAnalysis ? (
                              <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-white" />
                            ) : (
                              <AlertCircle className="h-4 w-4 md:h-5 md:w-5 text-white" />
                            )}
                          </div>
                          <div className="flex-1 lg:flex-none">
                            <Text
                              variant="h4"
                              className={`font-semibold text-sm md:text-base ${
                                canStartAnalysis
                                  ? "text-green-700 dark:text-green-300"
                                  : "text-amber-700 dark:text-amber-300"
                              }`}
                            >
                              {canStartAnalysis
                                ? "Hazır!"
                                : "Eksik dosyalar var"}
                            </Text>
                            <Text
                              variant="p"
                              size="sm"
                              className="text-gray-600 dark:text-gray-400"
                            >
                              {canStartAnalysis
                                ? t("assistant.essayGrader.readyToAnalyze")
                                : activeUploads > 0
                                  ? t("assistant.essayGrader.uploading")
                                  : !instructionsIncludedInRubric &&
                                      !uploadedFiles.some(
                                        (f) => f.type === "instructions",
                                      )
                                    ? t(
                                        "assistant.essayGrader.instructions.uploadRequired",
                                      )
                                    : t(
                                        "assistant.essayGrader.uploadRequirements",
                                      )}
                            </Text>
                          </div>
                        </div>
                        <Button
                          onClick={handleStartAnalysis}
                          disabled={!canStartAnalysis}
                          className={`w-full lg:w-auto px-6 md:px-8 py-3 rounded-xl font-semibold text-white transition-all duration-300 transform hover:scale-105 shadow-lg ${
                            canStartAnalysis
                              ? "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-blue-200 dark:shadow-blue-900"
                              : "bg-gray-400 dark:bg-gray-600 cursor-not-allowed opacity-60"
                          }`}
                          size="lg"
                        >
                          <div className="flex items-center justify-center gap-2">
                            <Upload className="h-4 w-4 md:h-5 md:w-5" />
                            <span
                              data-testid="start-analysis"
                              className="text-sm md:text-base"
                            >
                              {activeUploads > 0
                                ? t("assistant.essayGrader.uploading")
                                : t("assistant.essayGrader.startAnalysis")}
                            </span>
                          </div>
                        </Button>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}

              {/* Assignments Section - Only visible when no grading mode is selected */}
              {!gradingMode && renderAssignmentsSection()}
            </motion.div>
          )}

          {/* Loading Section */}
          {view === "loading" && (
            <motion.div
              key="loading"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 md:p-6"
            >
              <LoadingContainer
                showSteps={ANALYSIS_STEPS}
                duration={45}
                title={
                  t("assistant.essayGrader.analyzing") || "Analiz ediliyor..."
                }
                themeColor="blue"
              />
            </motion.div>
          )}

          {/* Results Section */}
          {view === "results" && (
            <motion.div
              key="results"
              ref={resultsRef}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 md:p-6"
            >
              <div className="mb-4 md:mb-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <BarChart4 className="h-4 w-4 md:h-5 md:w-5 text-blue-500" />
                    <Text
                      variant="h2"
                      size="lg"
                      className="font-semibold md:text-xl"
                    >
                      {t("assistant.essayGrader.resultsTitle") ||
                        "Analiz Sonuçları"}
                    </Text>
                  </div>

                  {essayGraderData &&
                    essayGraderData.results &&
                    essayGraderData.results.length > 0 && (
                      <div className="flex items-center bg-blue-50 dark:bg-blue-900/20 px-3 md:px-4 py-2 rounded-lg">
                        <FileText className="h-3 w-3 md:h-4 md:w-4 text-blue-500 mr-2" />
                        <Text
                          variant="span"
                          className="text-blue-700 dark:text-blue-300 font-medium text-sm md:text-base"
                        >
                          {essayGraderData.results.length}{" "}
                          {t("assistant.essayGrader.essaysAnalyzed") ||
                            "Kompozisyon analiz edildi"}
                        </Text>
                      </div>
                    )}
                </div>

                <Text
                  variant="p"
                  status="secondary"
                  className="mt-2 text-sm md:text-base"
                >
                  {t("assistant.essayGrader.resultsDescription") ||
                    "Analiz sonuçlarını inceleyin ve detaylarını görüntüleyin"}
                </Text>
              </div>

              {renderEssayTable()}

              <div className="mt-6 md:mt-8 flex justify-end gap-3">
                <Button
                  onClick={handleReset}
                  className="flex items-center"
                  bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
                  borderWidth={"thin"}
                  borderColor="border-gray-200 dark:border-gray-700"
                  textColor="text-gray-700 dark:text-gray-200"
                  size="sm"
                  rounded="lg"
                >
                  <Upload className="h-3 w-3 md:h-4 md:w-4 mr-2" />
                  <span className="text-sm md:text-base">
                    {t("assistant.essayGrader.newAnalysis") || "Yeni Analiz"}
                  </span>
                </Button>
              </div>
            </motion.div>
          )}

          {/* Details Section */}
          {view === "details" &&
            selectedEssayIndex !== null &&
            essayGraderData &&
            essayGraderData.results && (
              <motion.div
                key="details"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 md:p-6"
              >
                <div className="mb-4 flex flex-col gap-4">
                  {/* Back button */}
                  <div className="flex justify-start">
                    <Button
                      onClick={() => {
                        setView("results");
                        setActiveSection("results");
                        setSelectedEssayIndex(null);
                      }}
                      size="sm"
                      rounded="lg"
                      bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
                      textColor="text-gray-700 dark:text-gray-200"
                      className="flex items-center"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      {t("assistant.essayGrader.backToResults") ||
                        "Sonuçlara Dön"}
                    </Button>
                  </div>

                  {/* Essay title and navigation */}
                  <div className="flex flex-col items-center gap-3">
                    <Text
                      variant="h3"
                      size="lg"
                      className="font-semibold text-center md:text-xl"
                    >
                      {essayGraderData.results[selectedEssayIndex]
                        .essayFiles?.[0]?.fileName ||
                        `Kompozisyon ${selectedEssayIndex + 1}`}
                    </Text>

                    {/* Essay navigation */}
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => {
                          const newIndex = selectedEssayIndex - 1;
                          setSelectedEssayIndex(newIndex);
                        }}
                        disabled={selectedEssayIndex === 0}
                        size="sm"
                        rounded="lg"
                        bgColor="bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 disabled:bg-gray-50 dark:disabled:bg-gray-800"
                        textColor="text-gray-700 dark:text-gray-200 disabled:text-gray-400 dark:disabled:text-gray-500"
                        className="flex items-center disabled:cursor-not-allowed w-[100px] md:w-[120px] justify-center"
                      >
                        <ChevronLeft className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                        <span className="text-xs md:text-sm">
                          {t("assistant.essayGrader.previous") || "Önceki"}
                        </span>
                      </Button>

                      {/* Position indicator */}
                      <div className="bg-blue-50 dark:bg-blue-900/20 px-2 md:px-3 py-1.5 rounded-lg">
                        <Text
                          variant="span"
                          className="text-xs md:text-sm font-medium text-blue-700 dark:text-blue-300"
                        >
                          {selectedEssayIndex + 1}/
                          {essayGraderData.results.length}
                        </Text>
                      </div>

                      <Button
                        onClick={() => {
                          const newIndex = selectedEssayIndex + 1;
                          setSelectedEssayIndex(newIndex);
                        }}
                        disabled={
                          selectedEssayIndex ===
                          essayGraderData.results.length - 1
                        }
                        size="sm"
                        rounded="lg"
                        bgColor="bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 disabled:bg-gray-50 dark:disabled:bg-gray-800"
                        textColor="text-gray-700 dark:text-gray-200 disabled:text-gray-400 dark:disabled:text-gray-500"
                        className="flex items-center disabled:cursor-not-allowed w-[100px] md:w-[120px] justify-center"
                      >
                        <span className="text-xs md:text-sm">
                          {t("assistant.essayGrader.next") || "Sonraki"}
                        </span>
                        <ChevronRight className="h-3 w-3 md:h-4 md:w-4 ml-1" />
                      </Button>
                    </div>

                    {/* Open in chat button - centered on mobile */}
                    <Button
                      onClick={() => {
                        handleSelectChat(
                          Number(newChatId),
                          essayGraderData.results?.[selectedEssayIndex]?.id,
                        );
                      }}
                      size="sm"
                      rounded="lg"
                      bgColor="bg-blue-600 hover:bg-blue-700"
                      textColor="text-white"
                      className="flex items-center"
                    >
                      <ExternalLink className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                      <span className="text-xs md:text-sm">
                        {t("assistant.essayGrader.openInChat") || "Sohbette Aç"}
                      </span>
                    </Button>
                  </div>
                </div>

                <div className="mt-4">
                  <ChatMessageBox
                    message={essayGraderData.results[selectedEssayIndex].result}
                    t={t}
                    onNavigate={() => {
                      i18n.changeLanguage(teacherPreferencedLanguage);
                      handleSelectChat(
                        Number(newChatId),
                        essayGraderData.results?.[selectedEssayIndex]?.id,
                      );
                    }}
                  />
                </div>
              </motion.div>
            )}
        </AnimatePresence>

        <HistoryModal
          show={showHistory}
          onClose={() => setShowHistory(false)}
          chats={oldChats || []}
          onSelectChat={handleSelectChat}
          isLoading={!oldChatsRaw}
          onShowTable={(chatId) => {
            setView("results");
            setActiveSection("results");
            setSelectedEssayIndex(null);
            setShowHistory(false);
            setNewChatId(String(chatId));
          }}
        />
      </div>
    </div>
  );
};

export default EssayGradingView;
